import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Card, CardContent, Grid } from "@mui/material";

import { requestOptions, URL } from "../../Constants/GlobalConstant";
import { getUserSessionData } from "../../CommonFuntion/CheckSession";
import { sessionCache } from "../../CommonFuntion/sessionCache";
import {
  GetDeliveryAddress,
  GetInvoiceAddress,
  GetCountries,
} from "../../CommonFuntion/SchemaAPICalls";
import InvoiceAddressComponent from "./InvoiceAddressComponent";
import DeliveryAddressComponent from "./DeliveryAddressComponent";
import {
  handleOptionSelection,
  handleDeliveryChange,
  resetDeliveryAddress,
  validateForm,
  setDeliveryAddressValidationFalse,
} from "../../CommonFuntion/AddressFormComponentHelper";

const initialValidation = { msg: "", invalid: false };

const AddressFormComponent = forwardRef(
  (
    {
      isInteralOrAdminUser,
      divisionId,
      factoryConfigCustomerId,
      user_id,
      schema_id,
      SetDefaultDeliveryChild,
      setInvoiceAddressChild,
      setCustomerIdChild,
      setSelectedDeliveryChild,
      isCollectOrder,
      searchParams,
      setRequestNewAddressChild,
      setDelAddress1Child,
      setDelAddress2Child,
      setDelAddress3Child,
      setDelAddress4Child,
      setDelPostalCodeChild,
      setDelContactNameChild,
      setDelCountryCodeChild,
      setDelCustomerNameChild,
    },
    ref
  ) => {
    // State
    const [countries, setCountries] = useState([]);
    const [shipToID, setShipToID] = useState(0);
    const [post, setPost] = useState("");
    const [delivery, setDelivery] = useState([]);
    const [defaultDelivery, setDefaultDelivery] = useState("0");
    const [requestNewAddress, setRequestNewAddress] = useState(false);
    const [delCustomerName, setDelCustomerName] = useState("");
    const [delContactName, setDelContactName] = useState("");
    const [delAddress1, setDelAddress1] = useState("");
    const [delAddress2, setDelAddress2] = useState("");
    const [delAddress3, setDelAddress3] = useState("");
    const [delAddress4, setDelAddress4] = useState("");
    const [delPostalCode, setDelPostalCode] = useState("");
    const [delCountry, setDelCountry] = useState("");
    const [selectedOption, setSelectedOption] = useState(null);

    // Validation states
    const [companyNameValidation, setCompanyNameValidation] =
      useState(initialValidation);
    const [contactValidation, setContactValidation] =
      useState(initialValidation);
    const [address1Validation, setAddress1Validation] =
      useState(initialValidation);
    const [delAddressDropValidation, setDelAddressDropValidation] =
      useState(initialValidation);
    const [countryCodeValidation, setCountryCodeValidation] =
      useState(initialValidation);

    // Create wrapper functions for the helper functions
    const handleOptionSelectionWrapper = (option) => {
      return handleOptionSelection(
        option,
        isInteralOrAdminUser,
        divisionId,
        setSelectedOption,
        setDelivery,
        setPost,
        setInvoiceAddressChild,
        resetDeliveryAddressWrapper,
        setCustomerIdChild
      );
    };

    const handleDeliveryChangeWrapper = (event) => {
      return handleDeliveryChange(
        event,
        delivery,
        setDefaultDelivery,
        SetDefaultDeliveryChild,
        setDelAddressDropValidation,
        resetDeliveryAddressWrapper,
        setSelectedDeliveryChild,
        setDelContactName,
        setDelCustomerName,
        setDelAddress1,
        setDelAddress2,
        setDelAddress3,
        setDelAddress4,
        setDelCountry,
        setDelPostalCode,
        setShipToID
      );
    };

    const resetDeliveryAddressWrapper = () => {
      return resetDeliveryAddress(
        setDelAddress1,
        setDelAddress4,
        setDelCustomerName,
        setDelAddress2,
        setDelPostalCode,
        setDelContactName,
        setDelAddress3,
        setDelCountry,
        setDefaultDelivery,
        SetDefaultDeliveryChild,
        setShipToID,
        setSelectedDeliveryChild
      );
    };

    const validateFormWrapper = () => {
      return validateForm(
        selectedOption,
        isInteralOrAdminUser,
        requestNewAddress,
        isCollectOrder,
        delContactName,
        setContactValidation,
        delCustomerName,
        setCompanyNameValidation,
        delCountry,
        setCountryCodeValidation,
        delAddress1,
        setAddress1Validation,
        defaultDelivery,
        setDelAddressDropValidation
      );
    };

    const setDeliveryAddressValidationFalseWrapper = () => {
      return setDeliveryAddressValidationFalse(
        setCountryCodeValidation,
        setContactValidation,
        setCompanyNameValidation,
        setAddress1Validation,
        setDelAddressDropValidation
      );
    };

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      validateForm: validateFormWrapper,
      resetDeliveryAddress: resetDeliveryAddressWrapper,
      setDeliveryAddressValidationFalse:
        setDeliveryAddressValidationFalseWrapper,
    }));

    // Effects
    useEffect(() => {
       let firstPageID = JSON.parse(localStorage.getItem("firstPageID"))!==null? JSON.parse(localStorage.getItem("firstPageID")): searchParams.get("PageID");
      let jsonObjectId =searchParams.get("OCC") ? searchParams.get("PageID") : firstPageID
      getUserSessionData(
        user_id,
        jsonObjectId
      ).then((data2) => {
        for (const key in data2) {
          sessionCache.set(key, data2[key]);
        }
        const data = sessionCache.get(user_id + jsonObjectId + "checkoutForm") ?? null;
        if (data) {
          // When factory config is active and the customer/division changed,
          // skip restoring stale delivery address fields.
          const divisionChanged = factoryConfigCustomerId &&
            data["customerId"] && data["customerId"] !== factoryConfigCustomerId;
          if (divisionChanged) {
            setRequestNewAddress(data["requestNewAddress"]);
            setRequestNewAddressChild(data["requestNewAddress"]);
            setCustomerIdChild(factoryConfigCustomerId);
          } else {
            setDefaultDelivery(data["shipToID"]);
            SetDefaultDeliveryChild(data["shipToID"]);
            setSelectedDeliveryChild(data["DeliveryAddress"]);
            setRequestNewAddress(data["requestNewAddress"]);
            setRequestNewAddressChild(data["requestNewAddress"]);
            setDelAddress1Child(data["DelAddress1"]);
            setDelAddress2Child(data["DelAddress2"]);
            setDelAddress3Child(data["DelAddress3"]);
            setDelAddress4Child(data["DelAddress4"]);
            setDelPostalCodeChild(data["DelPostalCode"]);
            setDelCustomerNameChild(data["DelCustomerName"]);
            setDelContactNameChild(data["DelContactName"]);
            setDelCountryCodeChild(data["DelCountry"]);
            setDelAddress1(
            !data["requestNewAddress"]
              ? data["DeliveryAddress"]?.delivery_address1?.trim()
              : data["DelAddress1"]
          );
          setDelAddress4(
            !data["requestNewAddress"]
              ? data["DeliveryAddress"]?.delivery_address4?.trim()
              : data["DelAddress4"]
          );
          setDelAddress2(
            !data["requestNewAddress"]
              ? data["DeliveryAddress"]?.delivery_address2?.trim()
              : data["DelAddress2"]
          );
          setDelAddress3(
            !data["requestNewAddress"]
              ? data["DeliveryAddress"]?.delivery_address3?.trim()
              : data["DelAddress3"]
          );
          setDelPostalCode(
            !data["requestNewAddress"]
              ? data["DeliveryAddress"]?.delivery_post_code?.trim()
              : data["DelPostalCode"]
          );
          setDelCustomerName(
            !data["requestNewAddress"]
              ? data["DeliveryAddress"]?.delivery_customer_name?.trim()
              : data["DelCustomerName"]
          );
          setDelContactName(
            !data["requestNewAddress"]
              ? data["DeliveryAddress"]?.contact_name?.trim()
              : data["DelContactName"]
          );
          setDelCountry(
            !data["requestNewAddress"]
              ? data["DeliveryAddress"]?.delivery_country_code?.trim()
              : data["DelCountry"]
          );
          setShipToID(data["DeliveryAddress"]?.shipToID);
          setCustomerIdChild(data["customerId"]);
          }
        }
      });
      // eslint-disable-next-line
    }, []);

    const isAddressInitialMount = useRef(true);
    useEffect(() => {
      if (isAddressInitialMount.current) {
        isAddressInitialMount.current = false;
      } else {
        // Division changed — reset delivery address so stale address isn't submitted
        // Same reset as "Request New Address" checkbox unchecked
        setRequestNewAddress(false);
        setRequestNewAddressChild(false);
        resetDeliveryAddressWrapper();
        setDeliveryAddressValidationFalseWrapper();
        setDelAddress1Child("");
        setDelAddress2Child("");
        setDelAddress3Child("");
        setDelAddress4Child("");
        setDelPostalCodeChild("");
        setDelCustomerNameChild("");
        setDelContactNameChild("");
        setDelCountryCodeChild("");
      }
      if (!isInteralOrAdminUser) {
        // When factory/division config provides a customer ID, use it directly
        if (factoryConfigCustomerId) {
          setCustomerIdChild(factoryConfigCustomerId.trim());
          GetDeliveryAddress(factoryConfigCustomerId, divisionId).then(setDelivery);
          GetInvoiceAddress(factoryConfigCustomerId, divisionId).then((data) => {
            setPost(data);
            setInvoiceAddressChild(data);
          });
        } else {
          fetch(
            `${URL}/Customer/GetCustomerID?userID=${user_id}&divisionId=${divisionId}`,
            requestOptions,
            { mode: "no-cors" }
          )
            .then((response) => response.json())
            .then((customerId) => {
              if (customerId !== "undefined") {
                setCustomerIdChild(customerId?.trim());
                GetDeliveryAddress(customerId, divisionId).then(setDelivery);
                GetInvoiceAddress(customerId, divisionId).then((data) => {
                  setPost(data);
                  setInvoiceAddressChild(data);
                });
              }
            });
        }
      }
      GetCountries()
        .then(setCountries)
        .catch((error) => {
          console.error("Error fetching countries:", error);
        });
      // eslint-disable-next-line
    }, [divisionId, factoryConfigCustomerId]);

    // Render
    const checked = isCollectOrder;

    return (
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <InvoiceAddressComponent
              post={post}
              isInteralOrAdminUser={isInteralOrAdminUser}
              divisionId={divisionId}
              user_id={user_id}
              searchParams={searchParams}
              handleOptionSelection={handleOptionSelectionWrapper}
            />
            <DeliveryAddressComponent
              checked={checked}
              requestNewAddress={requestNewAddress}
              setRequestNewAddress={setRequestNewAddress}
              setRequestNewAddressChild={setRequestNewAddressChild}
              resetDeliveryAddress={resetDeliveryAddressWrapper}
              setDelAddressDropValidation={setDelAddressDropValidation}
              setDeliveryAddressValidationFalse={
                setDeliveryAddressValidationFalseWrapper
              }
              delivery={delivery}
              defaultDelivery={defaultDelivery}
              handleDeliveryChange={handleDeliveryChangeWrapper}
              delAddressDropValidation={delAddressDropValidation}
              delCustomerName={delCustomerName}
              setCompanyNameValidation={setCompanyNameValidation}
              setDelCustomerName={setDelCustomerName}
              setDelCustomerNameChild={setDelCustomerNameChild}
              companyNameValidation={companyNameValidation}
              delContactName={delContactName}
              setContactValidation={setContactValidation}
              setDelContactName={setDelContactName}
              setDelContactNameChild={setDelContactNameChild}
              contactValidation={contactValidation}
              delAddress1={delAddress1}
              setAddress1Validation={setAddress1Validation}
              setDelAddress1={setDelAddress1}
              setDelAddress1Child={setDelAddress1Child}
              address1Validation={address1Validation}
              delAddress2={delAddress2}
              setDelAddress2={setDelAddress2}
              setDelAddress2Child={setDelAddress2Child}
              delAddress3={delAddress3}
              setDelAddress3={setDelAddress3}
              setDelAddress3Child={setDelAddress3Child}
              delAddress4={delAddress4}
              setDelAddress4={setDelAddress4}
              setDelAddress4Child={setDelAddress4Child}
              delPostalCode={delPostalCode}
              setDelPostalCode={setDelPostalCode}
              setDelPostalCodeChild={setDelPostalCodeChild}
              countries={countries}
              delCountry={delCountry}
              setCountryCodeValidation={setCountryCodeValidation}
              setDelCountry={setDelCountry}
              setDelCountryCodeChild={setDelCountryCodeChild}
              countryCodeValidation={countryCodeValidation}
            />
          </Grid>
        </CardContent>
      </Card>
    );
  }
);

export default AddressFormComponent;
